import { runInAction, observable, makeObservable, toJS } from 'mobx';

import BaseListStore, { DEFAULT_LIMIT } from 'stores/BaseListStore';
import AlertStore, { IAlert } from 'stores/alert/AlertStore';
import { SortDirection } from 'constants/SortDirection';
import {
    getConditionArrayForPropertyArray,
    getConditionArrayFromObject,
    getDateBetweenConditions,
    getDefaultFilter,
    getFlagConditions
} from 'stores/_services/filterService';
import { IDataFetchOptions } from '../../types';
import { buildOptions, buildWhereFilter, getCount, getPage, post } from 'stores/_services/dataService';
import { buildCsvDownloadUrl, downloadFromPayload } from 'stores/_services/downloadService';
import { findById } from 'services/alertService';
import { formatBackslash } from 'stores/_helpers/formatBackslash';

export default class AlertListStore extends BaseListStore<AlertStore> {
    activeAlertCount = 0;
    itemForAction: IAlert | null = null;
    actionOnItem: string | null = null;
    listFilters = [
        'signal',
        'severity',
        'sourceType',
        'searchHostname',
        'searchName',
        'location',
        'organization',
        'searchIP',
        'searchPath',
        'searchOwner',
        'searchMitreTactic',
        'searchCommandLine',
        'eventTimeBetween',
        'lastSeenOnBetween',
        'createdOnBetween',
        'flag'
    ];
    showHistorical = false;

    constructor() {
        super({
            itemStore: AlertStore,
            sortBy: 'eventTime',
            sortDirection: SortDirection.DESC,
            defaultFilter: getDefaultFilter({ filters: ['archived', 'signal', 'severity'], includeSearch: false })
        });
        this.page = 0;

        makeObservable(this, {
            activeAlertCount: observable,
            actionOnItem: observable,
            showHistorical: observable
        });
    }

    addItemAction(item: IAlert, action: string): void {
        this.itemForAction = item;
        this.actionOnItem = action;

        this.selectedIds = { [item.id]: true };
    }

    clearItemAction(): void {
        this.itemForAction = null;
        this.actionOnItem = null;
    }

    async getData(args: IDataFetchOptions = {}, pageNumber = 0, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = this.getConditions(this.filter);

        if (args.sortBy === undefined) {
            this.clearSort();
        }

        const options = {
            filter: {
                limit: pageSize,
                skip: pageNumber * pageSize,
                where: conditions,
                order: this.sort,
                include: [
                    {
                        relation: 'response',
                        scope: {
                            order: 'createdOn asc',
                            fields: ['extensionId'],
                            include: {
                                relation: 'extension',
                                scope: {
                                    fields: ['name']
                                }
                            }
                        }
                    }
                ]
            }
        };

        const collectionName = this.showHistorical ? 'alertsAll' : 'alertDetails';

        const { data } = await getPage(collectionName, options);

        const totalCount = await getCount(collectionName, { where: conditions });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getAlert(alertId: string): Promise<void> {
        const data = await findById(alertId);
        this._updateData(data);
    }

    async getActiveAlertCount(): Promise<void> {
        const count = await getCount('alerts', { where: { archived: false, signal: true } });

        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.activeAlertCount = count;
        });
    }

    async archiveAlert(id: string, fetchList = false, pageNumber = 0, pageSize = DEFAULT_LIMIT): Promise<void> {
        const options = {
            queryParams: { where: { id } }
        };

        await post('alerts/archive', null, options);

        this.getActiveAlertCount();
        fetchList ? this.getData({ reset: true }, pageNumber, pageSize) : this.getAlert(id);
    }

    async archiveAlerts(args: { fetchList: boolean; pageNumber: number; pageSize: number; idsProp?: string[] }): Promise<void> {
        const { fetchList = false, pageNumber = 0, pageSize = DEFAULT_LIMIT, idsProp } = args;

        const ids = idsProp ? idsProp : this.selectedIdList;

        const options = {
            queryParams: {
                where: buildWhereFilter({
                    where: this.getListConditions(ids, this.filter)
                })
            }
        };

        await post('alerts/archive', null, options);

        this.deselectAll();
        this.getActiveAlertCount();
        fetchList ? this.getData({ reset: true }, pageNumber, pageSize) : this.getData({ reset: true }, 1, pageSize);
    }

    async unarchiveAlert(id: string, fetchList = false, pageNumber = 0, pageSize = DEFAULT_LIMIT): Promise<void> {
        const options = {
            queryParams: { where: { id } }
        };

        await post('alerts/unarchive', null, options);

        this.getActiveAlertCount();
        fetchList ? this.getData({ reset: true }, pageNumber, pageSize) : this.getAlert(id);
    }

    async unarchiveAlerts(args: { fetchList: boolean; pageNumber: number; pageSize: number; idsProp?: string[] }): Promise<void> {
        const { fetchList = false, pageNumber = 0, pageSize = DEFAULT_LIMIT, idsProp } = args;

        const ids = idsProp || this.selectedIdList;

        const options = {
            queryParams: {
                where: buildWhereFilter({
                    where: this.getListConditions(ids, this.filter)
                })
            }
        };

        await post('alerts/unarchive', null, options);

        this.deselectAll();
        this.getActiveAlertCount();
        fetchList ? this.getData({ reset: true }, pageNumber, pageSize) : this.getData({ reset: true }, 1, pageSize);
    }

    async unarchiveAll(pageSize = DEFAULT_LIMIT): Promise<void> {
        const conditions = this.getListConditions(this.selectedIdList, this.filter);

        const where = buildWhereFilter({
            where: conditions
        });

        await post('alerts/unarchive', null, { queryParams: { where } });

        this.deselectAll();
        this.getActiveAlertCount();
        this.getData({ reset: true }, 1, pageSize);
    }

    async exportAllAlertsCsv(): Promise<void> {
        const conditions = this.getListConditions(this.selectedIdList, this.filter);
        const options = {
            filter: {
                where: conditions,
                order: this.sort
            }
        };

        const collectionName = this.showHistorical ? 'alertsAll' : 'alertDetails';
        const query = buildOptions(options);

        window.location.href = await buildCsvDownloadUrl(collectionName, query);
    }

    async exportAlertCsv(alertId: string): Promise<void> {
        const alertStore = this.data.get(alertId);
        const flatAlert = (alertStore as AlertStore).flatten();
        const header = Object.keys(flatAlert);
        const values = header.map((fieldName) =>
            fieldName === 'rulebody' || fieldName === 'versionbody' ? flatAlert[fieldName] : JSON.stringify(flatAlert[fieldName])
        );

        const csv = [];
        csv.push(`${header.join(', ')} \r\n`);
        csv.push(values.join(', '));

        downloadFromPayload(`alertDetail(${alertId})`, csv, 'file', 'csv');
    }

    async exportAlertsCsv(): Promise<void> {
        const ids = this.selectedIdList;
        const conditions = this.getListConditions(ids, this.filter);
        const options = {
            filter: {
                where: conditions,
                order: this.sort
            }
        };

        const query = buildOptions(options);

        window.location.href = await buildCsvDownloadUrl('alertDetails', query);
    }

    getListConditions(items: string[], filter: any) {
        return items && items.length ? this.getItemListConditions(items) : this.getConditions(filter);
    }

    getItemListConditions(items: string[]) {
        return [{ id: { inq: items } }];
    }

    getConditions(filter: any) {
        const conditions = [
            ...this.getLocationConditions(filter),
            ...this.getOrganizationConditions(filter),
            ...this.getSignalConditions(filter),
            ...this.getSourceTypeConditions(filter),
            ...this.getSeverityConditions(filter),
            ...this.getDateConditions(filter),
            ...this.getArchivedConditions(filter),
            ...this.getSearchConditions(filter),
            ...this.getFlagConditions(filter)
        ];

        return conditions;
    }

    getLocationConditions(filter: any) {
        return filter.location?.id?.length ? [{ locationId: filter.location.id }] : [];
    }

    getOrganizationConditions(filter: any) {
        return filter.organization?.id?.length ? [{ organizationId: filter.organization.id }] : [];
    }

    getSignalConditions(filter: any) {
        if (!filter.signal) return [];
        const categoryCondition = { or: [] as { signal: boolean }[] };
        if (filter.signal.alert) categoryCondition.or.push({ signal: true });
        if (filter.signal.nonAlert) categoryCondition.or.push({ signal: false });
        return categoryCondition.or.length ? [categoryCondition] : [];
    }

    getSourceTypeConditions(filter: any) {
        const sourceTypes = Object.keys(filter.sourceType || {}).filter((k) => !!filter.sourceType[k]);

        if (sourceTypes.includes('av')) {
            sourceTypes.push('dns');
        }

        return sourceTypes.length ? [{ or: getConditionArrayForPropertyArray(sourceTypes, 'sourceType') }] : [];
    }

    getSeverityConditions(filter: any) {
        if (!filter.severity) return [];
        const severityCondition = { or: [] as { severity: string | null }[] };
        if (filter.severity.low) severityCondition.or.push({ severity: 'low' }, { severity: 'Low' });
        if (filter.severity.medium) severityCondition.or.push({ severity: 'medium' }, { severity: 'Medium' });
        if (filter.severity.high) severityCondition.or.push({ severity: 'high' }, { severity: 'High' });
        if (filter.severity.severe) severityCondition.or.push({ severity: 'severe' }, { severity: 'Severe' });
        if (filter.severity.suppressed) severityCondition.or.push({ severity: 'suppressed' });
        if (filter.severity.none) severityCondition.or.push({ severity: '' }, { severity: null });
        return severityCondition.or.length ? [severityCondition] : [];
    }

    getDateConditions(filter: any) {
        const dateConditions = [
            ...(getDateBetweenConditions(filter.eventTimeBetween, 'eventTime') || []),
            ...(getDateBetweenConditions(filter.createdOnBetween, 'createdOn') || []),
            ...(getDateBetweenConditions(filter.lastSeenOnBetween, 'lastSeenOn') || [])
        ];

        return dateConditions.length ? [{ and: dateConditions }] : [];
    }

    getArchivedConditions(filter: any) {
        const archivedCondition = getConditionArrayFromObject(filter.archived);

        return archivedCondition ? [{ or: archivedCondition }] : [];
    }

    getSearchConditions(filter: any) {
        const searchConditions = [];
        if (filter.searchHostname) searchConditions.push({ hostname: { like: `%${formatBackslash(filter.searchHostname)}%` } });
        if (filter.searchIP) searchConditions.push({ ip: { like: `%${formatBackslash(filter.searchIP)}%` } });
        if (filter.searchMitreTactic) searchConditions.push({ mitreTactic: { like: `%${formatBackslash(filter.searchMitreTactic)}%` } });
        if (filter.searchPath) searchConditions.push({ 'data.path': { like: `%${formatBackslash(filter.searchPath)}%` } });
        if (filter.searchCommandLine) searchConditions.push({ 'data.commandLine': { like: `%${formatBackslash(filter.searchCommandLine)}%` } });
        if (filter.searchName) searchConditions.push({ name: { like: `%${formatBackslash(filter.searchName)}%` } });
        if (filter.searchOwner) searchConditions.push({ 'data.owner': { like: `%${formatBackslash(filter.searchOwner)}%` } });
        return searchConditions;
    }

    getFlagConditions(filter: any) {
        const flagCondition = getFlagConditions(filter.flag);

        return flagCondition ? [{ or: flagCondition }] : [];
    }

    checkArchivalAlertCondition(filter: any) {
        if (filter?.archived_alerts?.include_archived_alerts) {
            return true;
        } else {
            return false;
        }
    }

    toggleShowHistorical() {
        this.showHistorical = !this.showHistorical;
    }
}
